import { useEffect, useState, type ReactElement } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useNotificationStore } from "@/stores/notificationStore";
import { Bell, Mail, Loader2, Megaphone } from "lucide-react";
import { Switch } from "@/components/ui/switch";

interface NotificationSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function NotificationSettingsDialog({
  open,
  onOpenChange,
}: NotificationSettingsDialogProps): ReactElement {
  const { preferences, fetchPreferences, updatePreferences, isLoading, error } =
    useNotificationStore();
  const [localPreferences, setLocalPreferences] = useState(preferences);

  useEffect(() => {
    if (open) {
      void fetchPreferences();
    }
  }, [open, fetchPreferences]);

  useEffect(() => {
    if (preferences) {
      setLocalPreferences(preferences);
    }
  }, [preferences]);

  const handleToggle = async (
    field: keyof NonNullable<typeof localPreferences>,
  ): Promise<void> => {
    if (!localPreferences) return;

    const previousPreferences = localPreferences;
    const newValue = !localPreferences[field];
    const preferencePatch: Partial<NonNullable<typeof localPreferences>> = {
      [field]: newValue,
    };

    if (field === "marketing_email_opt_in") {
      const now = new Date().toISOString();
      preferencePatch.marketing_email_opt_in_at = newValue ? now : null;
      preferencePatch.marketing_email_opt_out_at = newValue ? null : now;
    }

    const updated = { ...localPreferences, ...preferencePatch };
    setLocalPreferences(updated);

    const success = await updatePreferences(preferencePatch);
    if (!success) {
      setLocalPreferences(previousPreferences);
    }
  };

  if (!localPreferences) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              Nastavenia notifikácií
            </DialogTitle>
            <DialogDescription>Načítavam nastavenia...</DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Nastavenia notifikácií
          </DialogTitle>
          <DialogDescription>
            Spravujte, aké notifikácie chcete dostávať emailom a v aplikácii.
            Tieto nastavenia sú individuálne pre váš účet.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {error ? (
            <div
              className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"
              role="alert"
            >
              Nepodarilo sa uložiť nastavenia notifikácií. Skúste to prosím
              znova.
            </div>
          ) : null}
          {/* Všeobecné nastavenia */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Všeobecné nastavenia</CardTitle>
              <CardDescription>
                Zapnite alebo vypnite všetky emailové a aplikované notifikácie
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label
                    htmlFor="email_enabled"
                    className="flex items-center gap-2"
                  >
                    <Mail className="h-4 w-4" />
                    Emailové notifikácie
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Zapnite/vypnite všetky emailové notifikácie
                  </p>
                </div>
                <Switch
                  id="email_enabled"
                  checked={localPreferences.email_enabled}
                  onCheckedChange={() => {
                    void handleToggle("email_enabled");
                  }}
                  disabled={isLoading}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label
                    htmlFor="in_app_enabled"
                    className="flex items-center gap-2"
                  >
                    <Bell className="h-4 w-4" />
                    Aplikované notifikácie
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Zobrazenie notifikácií v aplikácii
                  </p>
                </div>
                <Switch
                  id="in_app_enabled"
                  checked={localPreferences.in_app_enabled}
                  onCheckedChange={() => {
                    void handleToggle("in_app_enabled");
                  }}
                  disabled={isLoading}
                />
              </div>
            </CardContent>
          </Card>

          {/* Emailové notifikácie podľa typu */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                Typy emailových notifikácií
              </CardTitle>
              <CardDescription>
                Vyberte, ktoré typy notifikácií chcete dostávať emailom
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="email_lesson_reservation">
                    Rezervácie lekcií
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Potvrdenie rezervácie skupinovej alebo súkromnej lekcie
                  </p>
                </div>
                <Switch
                  id="email_lesson_reservation"
                  checked={localPreferences.email_lesson_reservation}
                  onCheckedChange={() => {
                    void handleToggle("email_lesson_reservation");
                  }}
                  disabled={isLoading || !localPreferences.email_enabled}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="email_lesson_cancelled">
                    Zrušenia lekcií
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Upozornenie pri zrušení rezervácie alebo lekcie
                  </p>
                </div>
                <Switch
                  id="email_lesson_cancelled"
                  checked={localPreferences.email_lesson_cancelled}
                  onCheckedChange={() => {
                    void handleToggle("email_lesson_cancelled");
                  }}
                  disabled={isLoading || !localPreferences.email_enabled}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="email_lesson_reminder">
                    Pripomienky pred lekciou
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Automatická pripomienka 24 hodín pred lekciou
                  </p>
                </div>
                <Switch
                  id="email_lesson_reminder"
                  checked={localPreferences.email_lesson_reminder}
                  onCheckedChange={() => {
                    void handleToggle("email_lesson_reminder");
                  }}
                  disabled={isLoading || !localPreferences.email_enabled}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="email_payment_confirmed">
                    Potvrdenia platby
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Potvrdenie pri nákupu balíka alebo úspešnej platbe
                  </p>
                </div>
                <Switch
                  id="email_payment_confirmed"
                  checked={localPreferences.email_payment_confirmed}
                  onCheckedChange={() => {
                    void handleToggle("email_payment_confirmed");
                  }}
                  disabled={isLoading || !localPreferences.email_enabled}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="email_pair_invitation">Pozvánky na pár</Label>
                  <p className="text-sm text-muted-foreground">
                    Notifikácie o pozvánkach na párové lekcie
                  </p>
                </div>
                <Switch
                  id="email_pair_invitation"
                  checked={localPreferences.email_pair_invitation}
                  onCheckedChange={() => {
                    void handleToggle("email_pair_invitation");
                  }}
                  disabled={isLoading || !localPreferences.email_enabled}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="email_system_announcements">
                    Systémové oznámenia
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Dôležité oznámenia a aktualizácie zo školy
                  </p>
                </div>
                <Switch
                  id="email_system_announcements"
                  checked={localPreferences.email_system_announcements}
                  onCheckedChange={() => {
                    void handleToggle("email_system_announcements");
                  }}
                  disabled={isLoading || !localPreferences.email_enabled}
                />
              </div>
            </CardContent>
          </Card>

          {/* Marketing – oddelené od transakčných notifikácií */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Marketingové emaily</CardTitle>
              <CardDescription>
                Dobrovoľné novinky, kampane a tanečné tipy. Toto nastavenie
                neovplyvní dôležité emaily k rezerváciám a platbám.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label
                    htmlFor="marketing_email_opt_in"
                    className="flex items-center gap-2"
                  >
                    <Megaphone className="h-4 w-4" />
                    Chcem dostávať marketingové novinky
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Pozvánky, novinky AC Academy a výber voľných termínov.
                  </p>
                </div>
                <Switch
                  id="marketing_email_opt_in"
                  checked={Boolean(localPreferences.marketing_email_opt_in)}
                  onCheckedChange={() => {
                    void handleToggle("marketing_email_opt_in");
                  }}
                  disabled={isLoading}
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
}
